import { useEffect, useRef, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BrandedQRCanvas } from "@/components/BrandedQRCanvas";
import { Download, FileText, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";

export default function QRGenerator() {
  const [restaurant, setRestaurant] = useState(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    api.get("/restaurant").then((r) => setRestaurant(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!restaurant) return <DashboardLayout role="owner"><div className="text-muted-foreground">Loading…</div></DashboardLayout>;

  const FRONTEND_BASE = window.location.origin;
  const menuUrl = `${FRONTEND_BASE}/m/${restaurant.slug}`;

  const downloadPNG = () => {
    const canvas = canvasRef.current; if (!canvas) return;
    const a = document.createElement("a");
    a.download = `menumojo-${restaurant.slug}.png`;
    a.href = canvas.toDataURL("image/png");
    a.click();
  };

  const downloadPDF = () => {
    const canvas = canvasRef.current; if (!canvas) return;
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const w = 360; const h = (canvas.height / canvas.width) * w;
    const x = (pageWidth - w) / 2; const y = 100;
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(22);
    pdf.text(restaurant.name, pageWidth / 2, 70, { align: "center" });
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(11);
    pdf.text("Scan to view our digital menu", pageWidth / 2, 90, { align: "center" });
    pdf.addImage(imgData, "PNG", x, y, w, h);
    pdf.setFontSize(9);
    pdf.text(menuUrl, pageWidth / 2, y + h + 30, { align: "center" });
    pdf.save(`menumojo-${restaurant.slug}.pdf`);
  };

  const copyLink = async () => { await navigator.clipboard.writeText(menuUrl); toast.success("Copied"); };

  return (
    <DashboardLayout role="owner">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-orange mb-2">QR Code</div>
        <h1 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight">Your branded QR</h1>
        <p className="text-foreground/60 mt-2">Download, print, and put it on every table.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 items-start">
        <Card className="p-10 border border-border bg-white flex items-center justify-center" data-testid="qr-preview">
          <BrandedQRCanvas value={menuUrl} size={420} canvasRef={canvasRef} />
        </Card>

        <div className="space-y-6">
          <Card className="p-6 border border-border">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Live menu URL</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-xs bg-muted rounded-md px-3 py-2 truncate" data-testid="menu-url-display">{menuUrl}</code>
              <Button variant="outline" size="icon" onClick={copyLink} data-testid="copy-url-btn"><Copy className="w-4 h-4" /></Button>
              <a href={menuUrl} target="_blank" rel="noreferrer"><Button variant="outline" size="icon" data-testid="open-url-btn"><ExternalLink className="w-4 h-4" /></Button></a>
            </div>
          </Card>

          <Card className="p-6 border border-border">
            <h3 className="font-heading font-semibold mb-1">Download & print</h3>
            <p className="text-sm text-foreground/60 mb-5">Every export includes the &ldquo;Powered by MenuMojo&rdquo; branding at the bottom.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Button onClick={downloadPNG} className="bg-brand-orange hover:bg-[#E65C00] text-white rounded-full h-11" data-testid="download-png-btn">
                <Download className="w-4 h-4" /> Download PNG
              </Button>
              <Button onClick={downloadPDF} variant="outline" className="rounded-full h-11" data-testid="download-pdf-btn">
                <FileText className="w-4 h-4" /> Download PDF
              </Button>
            </div>
          </Card>

          <Card className="p-6 border border-border bg-brand-navy text-white">
            <h3 className="font-heading font-semibold">Tip</h3>
            <p className="text-sm text-white/70 mt-2">
              Place the QR on menus, posters, and table tents. Any change you make
              in your dashboard reflects instantly when a diner refreshes the page —
              no reprints needed.
            </p>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
